// System 8 — the browser side. A thin player over dsp.js: the buffers the game plays are the SAME
// arrays node measured offline. Created lazily on the first user gesture (autoplay policy — an
// AudioContext before a gesture logs a console warning, and the console gate is zero-tolerance).
// Shot mode never creates it: captures stay deterministic and silent.

import { rain, traffic, hum, buzz, servo, footstep } from './dsp.js';

export class Engine {
  constructor() { this.ctx = null; this.spatial = []; this.stepBufs = {}; this.lastStep = -1; }

  /** Call from a click/keydown handler. Idempotent. */
  start() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    const mk = (f32) => {
      const b = this.ctx.createBuffer(1, f32.length, 44100);
      b.getChannelData(0).set(f32);
      return b;
    };

    // Ambient bed: rain + distant traffic, non-positional.
    for (const [f32, gain] of [[rain('concrete'), 0.5], [traffic(), 0.34]]) {
      const src = this.ctx.createBufferSource();
      src.buffer = mk(f32); src.loop = true;
      const g = this.ctx.createGain(); g.gain.value = gain;
      src.connect(g).connect(this.ctx.destination);
      src.start();
    }

    // Positional loops: transformer hum at two utility boxes, ballast buzz under two sign runs,
    // rain-on-metal under the awning line, servo whine following the Sentinel.
    const addSpatial = (f32, x, z, base, follow) => {
      const src = this.ctx.createBufferSource();
      src.buffer = mk(f32); src.loop = true;
      const g = this.ctx.createGain(); g.gain.value = 0;
      const p = this.ctx.createStereoPanner();
      src.connect(g).connect(p).connect(this.ctx.destination);
      src.start();
      this.spatial.push({ g, p, x, z, base, follow, src });
    };
    addSpatial(hum(), -7.0, -3.5, 0.5);
    addSpatial(hum(), 6.8, 9.0, 0.4);
    addSpatial(buzz(), -6.9, -18.0, 0.4);
    addSpatial(buzz(), 6.9, 2.0, 0.35);
    addSpatial(rain('metal'), 6.5, -20.0, 0.45);
    addSpatial(servo(), 0, 0, 0.6, 'robot');

    for (const s of ['pavement', 'asphalt', 'tile', 'grate']) this.stepBufs[s] = mk(footstep(s));
  }

  /** Per frame: distance gain + crude pan from listener position/heading. */
  update(px, pz, yaw, robot) {
    if (!this.ctx) return;
    const rx = Math.cos(yaw), rz = -Math.sin(yaw);        // listener right vector
    for (const s of this.spatial) {
      if (s.follow === 'robot' && robot) {
        s.x = robot.pos.x; s.z = robot.pos.z;
        s.src.playbackRate.value = 0.7 + robot.speed * 0.35;
      }
      const dx = s.x - px, dz = s.z - pz;
      const d2 = dx * dx + dz * dz;
      s.g.gain.value = s.base / (1 + d2 / 30);
      const d = Math.sqrt(d2) + 1e-4;
      s.p.pan.value = Math.max(-1, Math.min(1, (dx * rx + dz * rz) / d));
    }
  }

  /** One footstep on a surface, slightly varied so it never machine-guns. */
  step(surface) {
    if (!this.ctx) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.stepBufs[surface] || this.stepBufs.pavement;
    src.playbackRate.value = 0.92 + Math.random() * 0.16;
    const g = this.ctx.createGain();
    g.gain.value = 0.5;
    src.connect(g).connect(this.ctx.destination);
    src.start();
  }
}
